import { AccessControl } from 'app/business-logic/security/KnownAccessControls';
import { useHasAccess } from 'app/hooks/use-has-access';
import { ReactNode, PropsWithChildren } from 'react';
import { UnauthorisedMessage } from './unauthorised-message';

type AccessControlGateProps = {
  requires: AccessControl | AccessControl[];
  determiner?: 'some' | 'every';
  fallback?: ReactNode;
};

/**
 * A component that gates access to its children based on user access controls.
 * @param props.requires - The access control(s) required to view the children.
 * @param props.determiner - The mode of access check: "some" (at least one) or "every" (all). Defaults to "every".
 * @param props.fallback - The component to render if access is denied. Defaults to UnauthorisedMessage.
 */
export const AccessControlGate = ({
  requires,
  determiner = 'every',
  fallback = <UnauthorisedMessage />,
  children,
}: PropsWithChildren<AccessControlGateProps>) => {
  const hasAccess = useHasAccess();
  const allowed = hasAccess(requires, determiner);
  if (!allowed) {
    if (!fallback) return null;
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
